import React, { useState, useEffect } from 'react';
import {
  Button,
  Input,
  Form,
  Table,
  Modal,
  message,
  Popconfirm,
  Space,
  Card,
  Row,
  Col,
  Typography,
  Switch,
  Tag,
  Spin,
  Statistic,
  Divider,
  DatePicker,
  Row as AntRow,
  Col as AntCol,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ArchiveOutlined,
  UndoOutlined,
  BarChartOutlined,
  PrinterOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { createSite, getSites, updateSite, archiveSite, unarchiveSite } from '../services/siteService';
import attendanceService from '../services/attendanceService';
import { themeConfig } from '../config/theme';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const SiteManagement = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Site Report State
  const [isReportModalVisible, setIsReportModalVisible] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportDateRange, setReportDateRange] = useState([
    dayjs().startOf('year'),
    dayjs().endOf('year')
  ]);
  const [expandedRows, setExpandedRows] = useState([]);
  const [isPrintModalVisible, setIsPrintModalVisible] = useState(false);

  useEffect(() => {
    fetchSites();
  }, [showArchived]);

  const fetchSites = async () => {
    setLoading(true);
    try {
      const response = await getSites(showArchived);
      setSites(response.sites || []);
    } catch (error) {
      messageApi.error('Failed to fetch sites');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (editingSite) {
        await updateSite(editingSite.id.split('#')[1], values);
        messageApi.success('Site updated successfully!');
      } else {
        await createSite(values);
        messageApi.success('Site added successfully!');
      }

      form.resetFields();
      setIsModalVisible(false);
      setEditingSite(null);
      fetchSites(); // Refresh the list
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'An error occurred';
      messageApi.error(editingSite ? `Failed to update site: ${errorMessage}` : `Failed to add site: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (site) => {
    setEditingSite(site);
    form.setFieldsValue(site);
    setIsModalVisible(true);
  };

  const handleArchive = async (siteId) => {
    try {
      await archiveSite(siteId.split('#')[1]);
      messageApi.success('Site archived successfully!');
      fetchSites(); // Refresh the list
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'An error occurred';
      messageApi.error(`Failed to archive site: ${errorMessage}`);
    }
  };

  const handleUnarchive = async (siteId) => {
    try {
      await unarchiveSite(siteId.split('#')[1]);
      messageApi.success('Site unarchived successfully!');
      fetchSites(); // Refresh the list
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'An error occurred';
      messageApi.error(`Failed to unarchive site: ${errorMessage}`);
    }
  };

  const showModal = () => {
    setEditingSite(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingSite(null);
    form.resetFields();
  };

  const handleToggleArchived = (checked) => {
    setShowArchived(checked);
  };

  const fetchSiteReport = async (siteId, startDate, endDate) => {
    setReportLoading(true);
    // Reset expanded rows when fetching new data
    setExpandedRows([]);
    try {
      // Format dates to YYYY-MM-DD format for the API
      const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD');
      const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD');
      
      const response = await attendanceService.getSiteReport(siteId, formattedStartDate, formattedEndDate);
      console.log('DEBUG: Site Report Response: ', response);
      // Transform the API response to match our expected format
      const transformedData = {
        siteInfo: sites.find(site => site.id === siteId),
        monthlyData: response.monthlyData || [],
        totalStats: response.totalStats || {
          totalAttendance: 0,
          totalPayments: 0
        }
      };
      
      setReportData(transformedData);
    } catch (error) {
      console.error('Error fetching site report:', error);
      messageApi.error('Failed to fetch site report data');
    } finally {
      setReportLoading(false);
    }
  };

  const showSiteReport = (site) => {
    setSelectedSite(site);
    // Set default date range: current month + 2 months before
    const currentMonth = dayjs();
    const twoMonthsAgo = currentMonth.subtract(2, 'month').startOf('month');
    const endMonth = currentMonth.endOf('month');
    
    setReportDateRange([twoMonthsAgo, endMonth]);
    setIsReportModalVisible(true);
    
    // Fetch report data for the selected site
    fetchSiteReport(site.id, twoMonthsAgo, endMonth);
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      const [startDate, endDate] = dates;
      const currentMonth = dayjs();
      
      // Validate: end date should not be beyond current month
      if (endDate.isAfter(currentMonth, 'month')) {
        messageApi.warning('Cannot select dates beyond the current month');
        return;
      }
      
      // Validate: start date should not be beyond current month
      if (startDate.isAfter(currentMonth, 'month')) {
        messageApi.warning('Cannot select dates beyond the current month');
        return;
      }
      
      setReportDateRange(dates);
      // Refresh report data with new date range
      if (selectedSite) {
        fetchSiteReport(selectedSite.id, startDate, endDate);
      }
    }
  };

  const handleRowExpand = (expanded, record) => {
    if (expanded) {
      // Add this month to expanded rows
      setExpandedRows(prev => [...prev, record.monthKey]);
    } else {
      // Remove this month from expanded rows
      setExpandedRows(prev => prev.filter(key => key !== record.monthKey));
    }
  };

  const handlePrint = () => {
    setIsPrintModalVisible(true);
  };

  const handlePrintContent = () => {
    const printContent = document.getElementById('printable-site-report');
    const originalContent = document.body.innerHTML;
    
    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  const handleDownloadCSV = () => {
    if (!reportData) return;

    // Prepare CSV data
    let csvContent = 'Site Report\n';
    csvContent += `Site: ${reportData.siteInfo?.name || 'N/A'}\n`;
    csvContent += `Date Range: ${reportDateRange[0]?.format('DD-MMM-YYYY')} to ${reportDateRange[1]?.format('DD-MMM-YYYY')}\n\n`;
    
    // Summary statistics
    csvContent += 'Summary Statistics\n';
    csvContent += 'Total Attendance,Total Payments\n';
    csvContent += `${reportData.totalStats.totalAttendance},${reportData.totalStats.totalPayments}\n\n`;
    
    // Monthly breakdown
    csvContent += 'Monthly Breakdown\n';
    csvContent += 'Month,Total Workers,Shift Count,Worker Payments\n';
    
    reportData.monthlyData.forEach(month => {
      csvContent += `${month.monthName},${month.totalWorkers},${month.attendance},${month.workerPayments}\n`;
    });
    
    csvContent += '\n';
    
    // Detailed worker information for each month
    reportData.monthlyData.forEach(month => {
      csvContent += `\nWorkers for ${month.monthName}\n`;
      csvContent += 'Worker Name,Employment Type,Shift Count,Total Payment\n';
      
      month.workerDetails.forEach(worker => {
        csvContent += `${worker.name},${worker.employmentType},${worker.shiftCount},${worker.totalPayment}\n`;
      });
      
      // Month total
      const monthTotalShifts = month.workerDetails.reduce((sum, worker) => sum + worker.shiftCount, 0);
      const monthTotalPayment = month.workerDetails.reduce((sum, worker) => sum + worker.totalPayment, 0);
      csvContent += `Month Total,${monthTotalShifts},${monthTotalPayment}\n`;
    });

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `site-report-${reportData.siteInfo?.name || 'site'}-${reportDateRange[0]?.format('MMM-YYYY')}-${reportDateRange[1]?.format('MMM-YYYY')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      title: 'Site Name',
      dataIndex: 'siteName',
      key: 'siteName',
      render: (text, record) => (
        <Space>
          <strong>{text}</strong>
          {record.isArchived && <Tag color="red">Archived</Tag>}
        </Space>
      ),
    },
    {
      title: 'Site Address',
      dataIndex: 'siteAddress',
      key: 'siteAddress',
      render: (text) => text || '-',
    },
    {
      title: 'Contact Person',
      dataIndex: 'contactPerson',
      key: 'contactPerson',
      render: (text) => text || '-',
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (text) => text || '-',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => text ? new Date(text).toLocaleDateString() : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="default"
            icon={<BarChartOutlined />}
            size="small"
            onClick={() => showSiteReport(record)}
          >
            Report
          </Button>
          {!record.isArchived && (
            <>
              <Button
                type="primary"
                icon={<EditOutlined />}
                size="small"
                onClick={() => handleEdit(record)}
              >
                Edit
              </Button>
              <Popconfirm
                title="Are you sure you want to archive this site?"
                onConfirm={() => handleArchive(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="primary"
                  icon={<DeleteOutlined />}
                  size="small"
                  danger
                >
                  Archive
                </Button>
              </Popconfirm>
            </>
          )}
          {record.isArchived && (
            <Popconfirm
              title="Are you sure you want to unarchive this site?"
              onConfirm={() => handleUnarchive(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="primary"
                icon={<UndoOutlined />}
                size="small"
              >
                Unarchive
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {contextHolder}
      <Title level={2}>Site Management</Title>

      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showModal}
              size="large"
              disabled={showArchived}
            >
              Add New Site
            </Button>
          </Col>
          <Col>
            <Space>
              <span>Show Archived Sites:</span>
              <Switch
                checked={showArchived}
                onChange={handleToggleArchived}
                checkedChildren="Yes"
                unCheckedChildren="No"
              />
            </Space>
          </Col>
        </Row>
      </Card>

      <Card
        title={showArchived ? 'Archived Sites' : 'Sites'}
      >
        <div style={{ marginBottom: 12 }}>
          <Input.Search
            placeholder={`Search ${showArchived ? 'archived ' : ''}sites by name, address or contact`}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 320 }}
            allowClear
          />
        </div>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={sites
              .filter(site => {
                const q = (searchText || '').toLowerCase();
                if (!q) return true;
                const name = (site.siteName || '').toLowerCase();
                const address = (site.siteAddress || '').toLowerCase();
                const contact = (site.contactPerson || '').toLowerCase();
                return name.includes(q) || address.includes(q) || contact.includes(q);
              })}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} sites`,
            }}
          />
        </Spin>
      </Card>

      <Modal
        title={editingSite ? 'Edit Site' : 'Add New Site'}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
        width={600}
      >
        <Spin spinning={modalLoading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              siteName: '',
              siteAddress: '',
              contactPerson: ''
            }}
          >
            <Form.Item
              name="siteName"
              label="Site Name"
              rules={[
                { required: true, message: 'Please enter site name' },
                { min: 2, message: 'Site name must be at least 2 characters' }
              ]}
            >
              <Input
                placeholder="Enter site name"
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="siteAddress"
              label="Site Address"
            >
              <Input
                placeholder="Enter site address (optional)"
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="contactPerson"
              label="Contact Person"
            >
              <Input
                placeholder="Enter contact person name (optional)"
                size="large"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  size="large"
                >
                  {editingSite ? 'Update Site' : 'Add Site'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Site Report Modal */}
      <Modal
        title={
          <Space>
            <BarChartOutlined />
            <span>Site Report - {selectedSite?.siteName}</span>
          </Space>
        }
        open={isReportModalVisible}
        onCancel={() => setIsReportModalVisible(false)}
        footer={null}
        destroyOnClose
        width={1000}
      >
        <Spin spinning={reportLoading}>
          {reportData && (
            <div>
              {/* Date Range Selector */}
              <Card style={{ marginBottom: 16 }}>
                <Row gutter={16} align="middle">
                  <Col>
                    <span>Date Range: </span>
                  </Col>
                  <Col>
                    <RangePicker
                      value={reportDateRange}
                      onChange={handleDateRangeChange}
                      picker="month"
                      disabledDate={(current) => {
                        // Disable future months beyond current month
                        return current && current.isAfter(dayjs(), 'month');
                      }}
                      placeholder={['Start Month', 'End Month']}
                    />
                  </Col>
                  <Col style={{ marginLeft: 'auto' }}>
                    <Space>
                      <Button 
                        type="primary" 
                        icon={<PrinterOutlined />} 
                        onClick={handlePrint}
                        disabled={!reportData}
                      >
                        Print Report
                      </Button>
                      <Button 
                        type="default" 
                        icon={<DownloadOutlined />} 
                        onClick={handleDownloadCSV}
                        disabled={!reportData}
                      >
                        Download CSV
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Card>

              {/* Summary Statistics */}
              <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={12}>
                  <Card>
                    <Statistic
                      title="Total Attendance"
                      value={reportData.totalStats.totalAttendance}
                      suffix="shifts"
                    />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card>
                    <Statistic
                      title="Total Worker Payments"
                      value={reportData.totalStats.totalPayments}
                      prefix="₹"
                      formatter={(value) => value.toLocaleString()}
                    />
                  </Card>
                </Col>
              </Row>

              {/* Monthly Breakdown Table */}
              <Card title="Monthly Breakdown">
                <Table
                  dataSource={reportData.monthlyData}
                  rowKey="monthKey"
                  pagination={false}
                  expandable={{
                    expandedRowKeys: expandedRows,
                    onExpand: handleRowExpand,
                    expandedRowRender: (record) => (
                      <div style={{ padding: '16px', backgroundColor: themeConfig.background.beige }}>
                        <h4 style={{ marginBottom: '16px' }}>Workers for {record.monthName}</h4>
                        <Table
                          dataSource={record.workerDetails}
                          rowKey="id"
                          pagination={false}
                          size="small"
                          columns={[
                            {
                              title: 'Worker Name',
                              dataIndex: 'name',
                              key: 'name',
                              render: (text) => <strong>{text}</strong>,
                            },
                            {
                              title: 'Employment Type',
                              dataIndex: 'employmentType',
                              key: 'employmentType',
                              render: (type) => (
                                <Tag color={type === 'FIXED' ? 'green' : 'blue'}>
                                  {type}
                                </Tag>
                              ),
                            },
                            {
                              title: 'Shift Count',
                              dataIndex: 'shiftCount',
                              key: 'shiftCount',
                              align: 'center',
                              render: (value) => `${value} shifts`,
                            },
                            {
                              title: 'Total Payment',
                              dataIndex: 'totalPayment',
                              key: 'totalPayment',
                              align: 'right',
                              render: (value) => <strong>₹{value.toLocaleString()}</strong>,
                            },
                          ]}
                          summary={(pageData) => {
                            const totalShifts = pageData.reduce((sum, record) => sum + record.shiftCount, 0);
                            const totalPayment = pageData.reduce((sum, record) => sum + record.totalPayment, 0);

                            return (
                              <Table.Summary.Row style={{ backgroundColor: themeConfig.status.infoBg, fontWeight: 'bold' }}>
                                <Table.Summary.Cell index={0} colSpan={2} style={{ fontWeight: 'bold' }}>
                                  <strong>Month Total</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2} align="center" style={{ fontWeight: 'bold' }}>
                                  <strong>{totalShifts} shifts</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={3} align="right" style={{ fontWeight: 'bold' }}>
                                  <strong style={{ color: themeConfig.status.info }}>₹{totalPayment.toLocaleString()}</strong>
                                </Table.Summary.Cell>
                              </Table.Summary.Row>
                            );
                          }}
                        />
                      </div>
                    ),
                    rowExpandable: (record) => record.workerDetails && record.workerDetails.length > 0,
                  }}
                  columns={[
                    {
                      title: 'Month',
                      dataIndex: 'monthName',
                      key: 'monthName',
                      render: (text) => <strong>{text}</strong>,
                    },
                    {
                      title: 'Total Workers',
                      dataIndex: 'totalWorkers',
                      key: 'totalWorkers',
                      align: 'center',
                    },
                    {
                      title: 'Shift Count',
                      dataIndex: 'attendance',
                      key: 'attendance',
                      align: 'center',
                      render: (value) => `${value} shifts`,
                    },
                    {
                      title: 'Worker Payments',
                      dataIndex: 'workerPayments',
                      key: 'workerPayments',
                      align: 'right',
                      render: (value) => `₹${value.toLocaleString()}`,
                    },
                  ]}
                  summary={(pageData) => {
                    const totalWorkers = pageData.reduce((sum, record) => sum + record.totalWorkers, 0);
                    const totalAttendance = pageData.reduce((sum, record) => sum + record.attendance, 0);
                    const totalPayments = pageData.reduce((sum, record) => sum + record.workerPayments, 0);

                    return (
                      <Table.Summary.Row style={{ backgroundColor: themeConfig.background.beige, fontWeight: 'bold' }}>
                        <Table.Summary.Cell index={0} style={{ fontWeight: 'bold' }} colSpan={2}>
                          <strong>GRAND TOTAL</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="center" style={{ fontWeight: 'bold' }}>
                          <strong>{totalWorkers}</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="center" style={{ fontWeight: 'bold' }}>
                          <strong>{totalAttendance} shifts</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="right" style={{ fontWeight: 'bold' }}>
                          <strong style={{ color: '#1890ff' }}>₹{totalPayments.toLocaleString()}</strong>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    );
                  }}
                />
              </Card>

              {/* Additional Insights */}
              <Card title="Insights" style={{ marginTop: 16 }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Statistic
                      title="Average Monthly Shifts"
                      value={Math.round(reportData.totalStats.totalAttendance / reportData.monthlyData.length)}
                      suffix="shifts/month"
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Average Monthly Payment"
                      value={Math.round(reportData.totalStats.totalPayments / reportData.monthlyData.length)}
                      prefix="₹"
                      formatter={(value) => value.toLocaleString()}
                      suffix="/month"
                    />
                  </Col>
                </Row>
              </Card>
            </div>
          )}
        </Spin>
      </Modal>

      {/* Print Modal */}
      <Modal
        title="Print Site Report"
        open={isPrintModalVisible}
        onCancel={() => setIsPrintModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsPrintModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrintContent}>
            Print
          </Button>,
        ]}
        width={800}
      >
        <div id="printable-site-report">
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <h2>Site Report</h2>
            <h3>{reportData?.siteInfo?.name || 'Site Name'}</h3>
            <p>Date Range: {reportDateRange[0]?.format('DD-MMM-YYYY')} to {reportDateRange[1]?.format('DD-MMM-YYYY')}</p>
          </div>

          {/* Summary Statistics */}
          <div style={{ marginBottom: '20px' }}>
            <h4>Summary Statistics</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
              <tbody>
                <tr>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', fontWeight: 'bold' }}>Total Attendance</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{reportData?.totalStats?.totalAttendance || 0} shifts</td>
                </tr>
                <tr>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', fontWeight: 'bold' }}>Total Worker Payments</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>₹{reportData?.totalStats?.totalPayments?.toLocaleString() || 0}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Monthly Breakdown */}
          <div style={{ marginBottom: '20px' }}>
            <h4>Monthly Breakdown</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
              <thead>
                <tr>
                  <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Month</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Total Workers</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Shift Count</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Worker Payments</th>
                </tr>
              </thead>
              <tbody>
                {reportData?.monthlyData?.map((month, index) => (
                  <tr key={index}>
                    <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{month.monthName}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>{month.totalWorkers}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>{month.attendance} shifts</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>₹{month.workerPayments.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Detailed Worker Information */}
          {reportData?.monthlyData?.map((month, monthIndex) => (
            <div key={monthIndex} style={{ marginBottom: '20px' }}>
              <h4>Workers for {month.monthName}</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
                <thead>
                  <tr>
                    <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Worker Name</th>
                    <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Employment Type</th>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Shift Count</th>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Total Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {month.workerDetails?.map((worker, workerIndex) => (
                    <tr key={workerIndex}>
                      <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{worker.name}</td>
                      <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{worker.employmentType}</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>{worker.shiftCount} shifts</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>₹{worker.totalPayment.toLocaleString()}</td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }} colSpan={2}>Month Total</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', fontWeight: 'bold' }}>
                      {month.workerDetails?.reduce((sum, worker) => sum + worker.shiftCount, 0)} shifts
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>
                      ₹{month.workerDetails?.reduce((sum, worker) => sum + worker.totalPayment, 0).toLocaleString()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))}

          {/* Grand Total */}
          <div style={{ marginTop: '20px', borderTop: '2px solid #333', paddingTop: '10px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr style={{ fontWeight: 'bold', fontSize: '16px' }}>
                  <td style={{ padding: '8px' }}>GRAND TOTAL</td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    {reportData?.monthlyData?.reduce((sum, month) => sum + month.totalWorkers, 0)} workers
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    {reportData?.totalStats?.totalAttendance || 0} shifts
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    ₹{reportData?.totalStats?.totalPayments?.toLocaleString() || 0}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SiteManagement;
